import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Box,
    Badge,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    Stack,
    Button,
    Divider,
    Tooltip,
} from "@mui/material";
import NotificationsRoundedIcon       from "@mui/icons-material/NotificationsRounded";
import NotificationsNoneRoundedIcon   from "@mui/icons-material/NotificationsNoneRounded";
import CheckCircleOutlineRoundedIcon  from "@mui/icons-material/CheckCircleOutlineRounded";
import { api } from "../util/api";
import dayjs from "dayjs";
import LoadingDots from "./common/LoadingDots";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const POLL_INTERVAL_MS = 30_000;

/**
 * 알람 종 아이콘 + 드롭다운 (인라인).
 *
 * 사이드바의 사용자 프로필 카드 안(이름·부서 옆)에 배치하기 위해 wrapper 없이 인라인으로 렌더.
 * 부모(프로필 카드)가 onClick 으로 라우팅을 처리하므로 stopPropagation 으로 클릭 전파를 차단한다.
 *
 * <h3>폴링</h3>
 * <p>30초 주기로 unread-count 호출. 드롭다운 열릴 때 본문 목록 별도 GET.
 */
export default function AppHeader() {
    const history = useHistory();
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);

    /** 미열람 카운트만 짧게 — 폴링용. */
    const fetchUnread = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE}/notifications/me/unread-count`);
            const data = res?.data ?? res ?? {};
            setUnread(Number(data.count ?? 0));
        } catch { /* 인증 만료/네트워크 — 조용히 무시. 다음 폴링에 재시도 */ }
    }, []);

    /** 드롭다운 열릴 때 본문 목록 fetch (전체 — 읽음/미열람 섞어 최신순). */
    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`${API_BASE}/notifications/me`, { page: 0, size: 20 });
            const data = res?.data ?? res ?? {};
            setItems(Array.isArray(data.content) ? data.content : []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // 폴링 — 마운트 + 30s 주기. unread count 는 드롭다운 열림 여부와 무관하게 항상 갱신.
    useEffect(() => {
        fetchUnread();
        pollRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [fetchUnread]);

    // 드롭다운이 열려있는 동안에는 목록도 함께 30s 주기로 다시 fetch.
    // (열어둔 채로 새 알람이 와도 즉시 보이도록. 닫으면 자동 해제.)
    useEffect(() => {
        if (!open) return;
        const id = setInterval(fetchList, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [open, fetchList]);

    const openMenu = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setOpen(true);
        fetchList();
    };
    const closeMenu = () => { setOpen(false); setAnchorEl(null); };

    const handleClickItem = async (n) => {
        // 1) 미열람이면 읽음 처리
        if (!n.readAt) {
            try { await api.patch(`${API_BASE}/notifications/${n.id}/read`); } catch {}
            setUnread(c => Math.max(0, c - 1));
            setItems(arr => arr.map(x => x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x));
        }
        // 2) 라우트 이동
        closeMenu();
        if (n.linkUrl) {
            // 백엔드가 "/approval/123" 같은 절대 경로로 만들어둠 — react-router push.
            history.push(n.linkUrl);
        }
    };

    const handleMarkAll = async () => {
        try {
            await api.patch(`${API_BASE}/notifications/me/read-all`);
            setUnread(0);
            const now = new Date().toISOString();
            setItems(arr => arr.map(x => x.readAt ? x : { ...x, readAt: now }));
        } catch {}
    };

    return (
        <>
            <Tooltip title="알람" placement="bottom">
                <IconButton
                    onClick={openMenu}
                    size="small"
                    sx={{
                        flexShrink: 0,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
                    }}
                >
                    <Badge
                        variant="dot"
                        invisible={unread === 0}
                        color="error"
                        overlap="circular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        {unread > 0
                            ? <NotificationsRoundedIcon sx={{ fontSize: 20, color: "#0f172a" }} />
                            : <NotificationsNoneRoundedIcon sx={{ fontSize: 20, color: "#64748b" }} />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { width: 380, maxHeight: 480, borderRadius: 1.5 } } }}
                // MUI Menu 는 Portal 로 body 에 렌더되지만 React 합성 이벤트는 React tree 로 버블된다.
                // AppHeader 가 사이드바 프로필 Box(onClick=push("/")) 안에 끼워져 있어, 메뉴 안 어떤 클릭이든
                // 막지 않으면 알람 push(linkUrl) 직후 프로필 Box 의 push("/") 가 덮어써서 홈으로만 이동.
                onClick={(e) => e.stopPropagation()}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                       sx={{ px: 2, py: 1.2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        알람
                        {unread > 0 && (
                            <Typography component="span"
                                        sx={{ ml: 1, color: "#ef4444", fontSize: 12, fontWeight: 600 }}>
                                {unread} 새 알람
                            </Typography>
                        )}
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<CheckCircleOutlineRoundedIcon />}
                        onClick={handleMarkAll}
                        disabled={unread === 0}
                        sx={{ fontSize: 11.5 }}
                    >
                        모두 읽음
                    </Button>
                </Stack>
                <Divider />
                {loading && (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}><LoadingDots/></Typography>
                    </Box>
                )}
                {!loading && items.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>알람이 없습니다.</Typography>
                    </Box>
                )}
                {!loading && items.map(n => {
                    const read = !!n.readAt;
                    return (
                        <MenuItem
                            key={n.id}
                            onClick={() => handleClickItem(n)}
                            sx={{
                                alignItems: "flex-start",
                                whiteSpace: "normal",
                                py: 1.2,
                                gap: 0.8,
                                borderLeft: read ? "3px solid transparent" : "3px solid #ef4444",
                                bgcolor: read ? "transparent" : "rgba(239, 68, 68, 0.04)",
                                "&:hover": { bgcolor: read ? "#f8fafc" : "rgba(239, 68, 68, 0.08)" },
                            }}
                        >
                            {/* 읽음 시그널 — 읽은 알람은 회색 체크, 미열람은 빨간 점.
                                항상 같은 자리에 표시해서 좌측 정렬이 흔들리지 않게 함. */}
                            <Box sx={{
                                width: 14, mt: 0.4,
                                display: "flex", justifyContent: "center", flexShrink: 0,
                            }}>
                                {read ? (
                                    <CheckCircleOutlineRoundedIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                                ) : (
                                    <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: "#ef4444" }} />
                                )}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: 13,
                                    fontWeight: read ? 500 : 700,
                                    color: read ? "#64748b" : "#0f172a",
                                    overflow: "hidden", textOverflow: "ellipsis",
                                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                }}>
                                    {n.title}
                                </Typography>
                                {n.message && (
                                    <Typography sx={{
                                        fontSize: 11.5,
                                        color: read ? "#94a3b8" : "#64748b",
                                        mt: 0.3,
                                        overflow: "hidden", textOverflow: "ellipsis",
                                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                    }}>
                                        {n.message}
                                    </Typography>
                                )}
                                <Typography sx={{
                                    fontSize: 10.5,
                                    color: read ? "#cbd5e1" : "#94a3b8",
                                    mt: 0.4,
                                }}>
                                    {n.actorLabel ? `${n.actorLabel} · ` : ""}{n.createdAt ? dayjs(n.createdAt).format("MM-DD HH:mm") : ""}
                                    {read && (
                                        <Box component="span" sx={{ ml: 0.8, color: "#cbd5e1" }}>· 읽음</Box>
                                    )}
                                </Typography>
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}
